import { useState } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { Save, Upload, AlertTriangle, CheckCircle, Download, FileJson } from 'lucide-react';

const COLLECTIONS = ['events', 'announcements', 'blogs', 'magazines', 'press', 'team'];

const DataBackup = () => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');

    // --- EXPORT (BACKUP) ---
    const handleBackup = async () => {
        setLoading(true);
        setStatus('📦 Veriler hazırlanıyor...');

        try {
            const allData = {};

            for (const colName of COLLECTIONS) {
                setStatus(`📥 ${colName} verileri çekiliyor...`);
                const snapshot = await getDocs(collection(db, colName));
                allData[colName] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
            }

            // Create JSON file
            const dataStr = JSON.stringify(allData, null, 2);
            const blob = new Blob([dataStr], { type: "application/json" });
            const url = URL.createObjectURL(blob);

            // Trigger Download
            const link = document.createElement('a');
            link.href = url;
            link.download = `turet_yedek_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setStatus('✅ Yedekleme dosyası indirildi.');

        } catch (error) {
            console.error(error);
            setStatus(`❌ Hata: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // --- IMPORT (RESTORE) ---
    const handleRestore = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!window.confirm("DİKKAT! Bu işlem mevcut veritabanını tamamen SİLECEK ve yüklediğiniz yedekten geri döndürecektir. Emin misiniz?")) {
            event.target.value = ''; // Reset input
            return;
        }

        setLoading(true);
        setStatus('📂 Dosya okunuyor...');

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const backupData = JSON.parse(e.target.result);

                // Validate structure roughly
                const hasValidKeys = Object.keys(backupData).some(k => COLLECTIONS.includes(k));
                if (!hasValidKeys) throw new Error("Geçersiz yedek dosyası formatı.");

                // 1. Clear existing collections
                for (const colName of COLLECTIONS) {
                    setStatus(`🧹 ${colName} temizleniyor...`);
                    const snapshot = await getDocs(collection(db, colName));

                    if (snapshot.size > 0) {
                        const chunks = [];
                        for (let i = 0; i < snapshot.docs.length; i += 400) {
                            chunks.push(snapshot.docs.slice(i, i + 400));
                        }

                        for (const chunk of chunks) {
                            const batch = writeBatch(db);
                            chunk.forEach(doc => batch.delete(doc.ref));
                            await batch.commit();
                        }
                    }
                }

                // 2. Write new data
                for (const [colName, items] of Object.entries(backupData)) {
                    if (!COLLECTIONS.includes(colName)) continue;
                    if (!Array.isArray(items)) continue;

                    setStatus(`✍️ ${colName} geri yükleniyor (${items.length} adet)...`);

                    // Batch write (max 500)
                    const chunks = [];
                    for (let i = 0; i < items.length; i += 400) {
                        chunks.push(items.slice(i, i + 400));
                    }

                    for (const chunk of chunks) {
                        const batch = writeBatch(db);
                        chunk.forEach(item => {
                            const { id, ...data } = item;
                            // Use original ID if possible, or auto-id if not
                            const docRef = id ? doc(db, colName, id) : doc(collection(db, colName));
                            batch.set(docRef, data); // 'set' allows specifying ID
                        });
                        await batch.commit();
                    }
                }

                setStatus('✨ Geri yükleme başarıyla tamamlandı!');

            } catch (error) {
                console.error(error);
                setStatus(`❌ Geri yükleme hatası: ${error.message}`);
            } finally {
                setLoading(false);
                event.target.value = ''; // Reset input
            }
        };
        reader.readAsText(file);
    };

    return (
        <div className="p-8 text-gray-100 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Save className="text-cyan-400" />
                Veri Yedekleme ve Kurtarma
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Backup Card */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-cyan-500/50 transition-colors">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full bg-green-500/20 text-green-400">
                            <Download size={32} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">Yedek Al (Export)</h3>
                            <p className="text-sm text-gray-400">Tüm verileri JSON dosyası olarak indir.</p>
                        </div>
                    </div>

                    <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                        Mevcut veritabanındaki tüm etkinlikleri, duyuruları, blogları ve ekip üyelerini bilgisayarınıza indirir.
                        <br /><span className="text-xs text-yellow-500">Not: Resim dosyalarını indirmez, sadece veritabanı kayıtlarını indirir.</span>
                    </p>

                    <button
                        onClick={handleBackup}
                        disabled={loading}
                        className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? <span className="animate-spin">⏳</span> : <FileJson />}
                        Yedek İndir (.json)
                    </button>
                </div>

                {/* Restore Card */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-red-500/50 transition-colors">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full bg-red-500/20 text-red-400">
                            <Upload size={32} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">Geri Yükle (Import)</h3>
                            <p className="text-sm text-gray-400">Yedek dosyasından verileri yükle.</p>
                        </div>
                    </div>

                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-6 flex items-start gap-3">
                        <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={18} />
                        <p className="text-xs text-red-200">
                            <strong>DİKKAT:</strong> Bu işlem mevcut tüm verileri siler ve yedek dosyasındaki verilerin üzerine yazar. İşlem geri alınamaz.
                        </p>
                    </div>

                    <label className={`w-full py-3 px-4 ${loading ? 'bg-gray-600' : 'bg-red-600 hover:bg-red-700'} text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 cursor-pointer`}>
                        {loading ? (
                            <>
                                <span className="animate-spin">⏳</span>
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <Upload />
                                Yedek Yükle ve Sıfırla
                            </>
                        )}
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleRestore}
                            disabled={loading}
                            className="hidden"
                        />
                    </label>
                </div>
            </div>

            {/* Status Log */}
            {status && (
                <div className="bg-black/50 rounded-xl p-4 border border-gray-700 font-mono text-sm">
                    <div className="flex items-center gap-2 mb-2 text-gray-400">
                        <CheckCircle size={14} />
                        <span>İşlem Durumu:</span>
                    </div>
                    <p className="text-green-400 whitespace-pre-wrap">{status}</p>
                </div>
            )}
        </div>
    );
};

export default DataBackup;
